import './App.css'
import './i18n'; // Import i18n configuration
import { useEffect, useState } from 'react';
import { AuthProvider } from './presentation/contexts'
import { DIProvider } from './presentation/contexts/DIContext'
import AppRouter from './presentation/components/AppRouter'
import { OfflinePosProvider } from './offline/OfflinePosContext'
import container from './di/container';
import { TYPES } from './di/types';
import type { DatabaseDataSource } from './data/datasources/database/DatabaseDataSource';
import { supportsOfflinePos } from './runtime/frontofficeRuntime';
import { DesktopCloseButton } from './presentation/components/DesktopCloseButton';

function App() {
  const offlineSupported = supportsOfflinePos();
  const [isDatabaseReady, setIsDatabaseReady] = useState(!offlineSupported);

  useEffect(() => {
    if (!offlineSupported) {
      return;
    }

    // Only Electron owns a local database and offline synchronization.
    const initializeDatabase = async () => {
      try {
        console.log('🚀 Initializing Electron offline database...');
        const dataSource = container.get<DatabaseDataSource>(TYPES.DatabaseDataSource);
        
        const initialized = await dataSource.initialize();
        setIsDatabaseReady(initialized);
        
        if (initialized) {
          console.log('✅ Database initialized successfully');
        } else {
          console.warn('⚠️ Database initialization failed, app may have limited functionality');
        }
      } catch (error) {
        console.error('❌ Failed to initialize database:', error);
        setIsDatabaseReady(false);
      }
    };

    initializeDatabase();

    // Cleanup on unmount
    return () => {
      const dataSource = container.get<DatabaseDataSource>(TYPES.DatabaseDataSource);
      dataSource.close().catch(console.error);
    };
  }, [offlineSupported]);

  // This state is Electron-only; the online web app renders immediately.
  if (!isDatabaseReady) {
    return (
      <div className="flex items-center justify-center h-screen">
        <DesktopCloseButton />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing offline database...</p>
        </div>
      </div>
    );
  }

  return (
    <DIProvider>
      <AuthProvider>
        <OfflinePosProvider>
          <div className="App">
            <AppRouter />
          </div>
        </OfflinePosProvider>
      </AuthProvider>
    </DIProvider>
  )
}

export default App 